import { AnimatePresence, motion } from 'framer-motion';
import { Translate } from 'i18n';
import { FC, useEffect, useRef } from 'react';
import { useTypedDispatch, useTypedSelector } from 'students/stores/_utils';
import {
  fetchArticles,
  fetchMoreArticles,
  articlesActions
} from 'students/stores/articles/slice';
import {
  selectArticles,
  selectArticleError,
  selectArticlesIsLoading,
  selectArticlesIsLoadingMore,
  selectArticlesNextPage,
  selectArticlesPanelOpen
} from 'students/stores/articles/selectors';
import WhatsNewItem from './WhatsNewItem';

const PANEL_TITLE_ID = 'whats-new-panel-title';

const WhatsNewPanel: FC = () => {
  const dispatch = useTypedDispatch();
  const isOpen = useTypedSelector(selectArticlesPanelOpen);
  const articles = useTypedSelector(selectArticles);
  const isLoading = useTypedSelector(selectArticlesIsLoading);
  const isLoadingMore = useTypedSelector(selectArticlesIsLoadingMore);
  const nextPage = useTypedSelector(selectArticlesNextPage);
  const error = useTypedSelector(selectArticleError);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isOpen && articles.length === 0 && !isLoading && !error) {
      dispatch(fetchArticles());
    }
  }, [isOpen, articles.length, isLoading, error, dispatch]);

  useEffect(() => {
    if (isOpen) {
      closeButtonRef.current?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (isOpen && e.key === 'Escape') {
        dispatch(articlesActions.closePanel());
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, dispatch]);

  const handleClose = () => dispatch(articlesActions.closePanel());

  const handleShowMore = () => {
    if (nextPage) dispatch(fetchMoreArticles(nextPage));
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            key="backdrop"
            className="ln-fixed ln-inset-0 ln-z-[200] ln-bg-black/30"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={handleClose}
          />

          <motion.aside
            key="panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby={PANEL_TITLE_ID}
            className="ln-fixed ln-top-0 ln-right-0 ln-z-[201] ln-h-full ln-w-full ln-max-w-[400px] ln-bg-white ln-shadow-2xl ln-flex ln-flex-col"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.25, ease: 'easeInOut' }}
          >
            <header className="ln-flex ln-items-center ln-justify-between ln-px-5 ln-py-4 ln-border-b ln-border-gray-100 ln-shrink-0">
              <h2
                id={PANEL_TITLE_ID}
                className="ln-text-base ln-font-bold ln-text-gray-900"
              >
                <Translate
                  str="frontend.whats_new.panel_heading"
                  params={{ defaultValue: "✨ What's new in Lingu?" }}
                />
              </h2>
              <button
                ref={closeButtonRef}
                type="button"
                onClick={handleClose}
                aria-label="Close"
                className="ln-flex ln-items-center ln-justify-center ln-w-8 ln-h-8 ln-rounded-full ln-text-gray-500 hover:ln-bg-gray-100 ln-transition-colors ln-duration-150 ln-border-0 ln-bg-transparent ln-cursor-pointer ln-text-xl ln-leading-none"
              >
                ×
              </button>
            </header>

            <div className="ln-flex-1 ln-overflow-y-auto">
              {isLoading ? (
                <div className="ln-flex ln-items-center ln-justify-center ln-h-32">
                  <div className="ln-w-6 ln-h-6 ln-rounded-full ln-border-2 ln-border-[#05C3F1] ln-border-t-transparent ln-animate-spin" />
                </div>
              ) : articles.length === 0 ? (
                <p className="ln-text-sm ln-text-gray-400 ln-text-center ln-px-5 ln-py-12">
                  <Translate
                    str="frontend.whats_new.empty"
                    params={{ defaultValue: 'No articles yet.' }}
                  />
                </p>
              ) : (
                <ul className="ln-divide-y ln-divide-gray-100 ln-list-none ln-m-0 ln-p-0">
                  {articles.map((article) => (
                    <WhatsNewItem
                      key={article.id}
                      article={article}
                      onClose={handleClose}
                    />
                  ))}
                </ul>
              )}

              {nextPage && !isLoading && (
                <div className="ln-px-5 ln-py-4">
                  <button
                    type="button"
                    onClick={handleShowMore}
                    disabled={isLoadingMore}
                    className="ln-w-full ln-h-10 ln-rounded-xl ln-border ln-border-gray-200 ln-text-sm ln-font-medium ln-text-gray-600 hover:ln-bg-gray-50 ln-transition-colors ln-duration-150 ln-cursor-pointer ln-bg-white disabled:ln-opacity-50"
                  >
                    {isLoadingMore ? (
                      <span className="ln-inline-block ln-w-4 ln-h-4 ln-rounded-full ln-border-2 ln-border-gray-400 ln-border-t-transparent ln-animate-spin" />
                    ) : (
                      <Translate
                        str="frontend.whats_new.show_more"
                        params={{ defaultValue: 'Show more' }}
                      />
                    )}
                  </button>
                </div>
              )}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default WhatsNewPanel;
